<script lang="ts">
import { defineComponent } from 'vue'

// eslint-disable-next-line no-restricted-syntax
export enum ButtonColorVariant {
  GRAY = 'gray',
  PURPLE = 'purple',
  RED = 'red',
  GREEN = 'green',
  ORANGE = 'orange',
}

// eslint-disable-next-line no-restricted-syntax
export enum ButtonVariant {
  FILLED = 'filled',
  OUTLINED = 'outlined',
}

// eslint-disable-next-line no-restricted-syntax
export enum ButtonShapeVariant {
  ROUNDED = 'rounded',
  MEDIUM = 'medium',
  SQUARE = 'square',
}

export default defineComponent({
  name: 'SancareButton',
})
</script>

<script lang="ts" setup>
import { type Ref, ref, toRef } from 'vue'

import useFixedDimensions from '../misc/composables/fixedDimensions'
import SancareOcticon from './SancareOcticon.vue'
import { ComponentSize } from './types'

export interface SancareButtonProps {
  color?: ButtonColorVariant
  endIcon?: string | null
  isDisabled?: boolean
  isLoading?: boolean
  isWidthFixedOnLoading?: boolean
  shapeVariant?: ButtonShapeVariant
  startIcon?: string | null
  size?: ComponentSize
  text: string
  variant?: ButtonVariant
}

const props = withDefaults(defineProps<SancareButtonProps>(), {
  color: ButtonColorVariant.GRAY,
  endIcon: null,
  isDisabled: false,
  isLoading: false,
  isWidthFixedOnLoading: true,
  shapeVariant: ButtonShapeVariant.ROUNDED,
  startIcon: null,
  size: ComponentSize.SMALL,
  variant: ButtonVariant.FILLED,
})

const buttonRef: Ref<HTMLDivElement | null> = ref(null)
const { elementWidth }: { elementWidth: Ref<string | null> } = useFixedDimensions(
  toRef(props, 'isLoading'),
  buttonRef,
  { fixWidth: true, fixHeight: false }
)

const emit = defineEmits<{
  (e: 'click'): void
}>()

function handleClick(): void {
  if (!props.isDisabled && !props.isLoading) {
    emit('click')
  }
}
</script>

<template>
  <div
    ref="buttonRef"
    class="btn sancare-btn"
    :class="[
      {
        'disabled': isDisabled,
        'sancare-btn--loading': isLoading,
        'sancare-btn--icon-only': (startIcon || endIcon) && !text,
        'sancare-btn--rounded': shapeVariant === ButtonShapeVariant.ROUNDED,
        'sancare-btn--square': shapeVariant === ButtonShapeVariant.SQUARE,
        'sancare-btn--outlined': variant === ButtonVariant.OUTLINED,
        'sancare-btn--medium': size === ComponentSize.MEDIUM,
      },
      `sancare-btn--${color}`
    ]"
    :style="{minWidth: isWidthFixedOnLoading ? elementWidth : null}"
    @click="handleClick"
  >
    <span
      v-if="isLoading"
      class="loader loader-btn-style my-0 mx-auto"
    />
    <span
      v-else
      class="sancare-btn__content"
    >
      <div class="sancare-btn__content__start-icon-and-text">
        <sancare-octicon
          v-if="startIcon"
          :name="startIcon"
        />
        {{ text }}
      </div>
      <sancare-octicon
        v-if="endIcon"
        class="sancare-btn__content__end-icon"
        :name="endIcon"
      />
    </span>
  </div>
</template>
